<template>
  <div>
    <template v-if="videoType">
      <VideoSelector v-model="form.video_source" />
      <el-form-item
        v-if="VideoSource.File === form.video_source"
        :error="errors.video"
        prop="video"
        label="Upload video file"
        size="small"
      >
        <FileUpload
          :multiple="false"
          :tooltip="VIDEO_UPLOAD_TOOLTIP"
          allowed-formats=".mp4"
          @handleChange="
            (file, ref, list) => handleFileChange(file, ref, list, 'video')
          "
        />
      </el-form-item>
      <el-form-item
        v-if="VideoSource.Url === form.video_source"
        :error="errors.remote_url"
        prop="remote_url"
        label="Add video URL"
      >
        <RemoteVideoInput
          v-model="form.remote_url"
          @update:modelValue="update"
        />
      </el-form-item>
      <el-form-item
        v-if="VideoSource.Tag === form.video_source"
        :error="errors.vast_tag || errors.video"
        prop="vast_tag"
        label="Add VAST tag"
      >
        <VastTagInput v-model="form.vast_tag" @update:modelValue="update" />
      </el-form-item>
      <el-form-item
        v-if="VideoSource.Tag !== form.video_source"
        :error="errors.skip_offset"
        prop="skip_offset"
        label="Skip offset"
      >
        <SkipOffsetInput
          v-model="form.skip_offset"
          @update:modelValue="update"
        />
      </el-form-item>
    </template>
    <el-form-item
      v-if="
        (isStaff && AdType.Banner === form.type_id) || (isStaff && linkActive)
      "
      label="Sub Type"
      prop="sub_type"
    >
      <el-radio-group v-model="selectedSubType" @change="onSubTypeChange">
        <el-radio-button label="url">URL</el-radio-button>
        <el-radio-button label="code">Code</el-radio-button>
      </el-radio-group>
    </el-form-item>
    <el-form-item
      v-if="linkActive && shouldShowLinkUrl"
      :error="errors.link_url"
      :label="linkLabel"
      prop="link_url"
    >
      <LinkUrlInput v-model="form.link_url" @update:modelValue="update" />
    </el-form-item>
    <el-form-item
      v-if="extendedSettingsAllowed"
      label="Impression tracking URL"
    >
      <el-input
        v-model="form.impression_url"
        type="text"
        @update:modelValue="update"
      />
    </el-form-item>
    <el-form-item v-if="shouldShowCode" label="Code" prop="code">
      <CodeInput v-model="form.code" @update:modelValue="update" />
    </el-form-item>
    <template v-if="AdType.Banner === form.type_id">
      <el-form-item
        :error="errors.image"
        label="Image"
        prop="image"
        size="small"
      >
        <FileUpload
          :multiple="false"
          :tooltip="IMAGE_UPLOAD_TOOLTIP"
          allowed-formats=".jpg, .jpeg, .png, .gif, .mp4"
          @handleChange="
            (file, ref, list) => handleFileChange(file, ref, list, 'image')
          "
        />
      </el-form-item>
    </template>
    <template v-if="AdType.InpagePush === form.type_id">
      <el-form-item :error="errors.icon" label="Icon" size="small" prop="icon">
        <FileUpload
          :multiple="false"
          :tooltip="ICON_UPLOAD_TOOLTIP"
          allowed-formats=".jpg, .jpeg, .png, .gif, .mp4"
          @handleChange="
            (file, ref, list) => handleFileChange(file, ref, list, 'icon')
          "
        />
      </el-form-item>
      <el-form-item prop="title" :error="errors.title" label="Title">
        <el-input
          v-model="form.title"
          type="text"
          @update:modelValue="update"
        />
      </el-form-item>
      <el-form-item
        prop="description"
        :error="errors.description"
        label="Description"
      >
        <el-input
          v-model="form.description"
          type="textarea"
          @update:modelValue="update"
        />
      </el-form-item>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue'
import {
  AdType,
  VideoSource,
  FlatDealValidationErrorsKey,
  FlatDealType
} from '@/api/types'
import LinkUrlInput from '@/apps/traforama/components/LinkUrlInput.vue'
import useValidation from '@/hooks/useValidation'

import FileUpload from '@/components/FileUpload.vue'
import VideoSelector from '@/apps/traforama/views/creatives/components/VideoSelector.vue'
import VastTagInput from '@/apps/traforama/components/VastTagInput.vue'
import RemoteVideoInput from '@/apps/traforama/components/RemoteVideoInput.vue'
import SkipOffsetInput from '@/apps/traforama/components/SkipOffsetInput.vue'
import CodeInput from '@/apps/traforama/components/CodeInput.vue'
import useResize from '@/hooks/useResize'
import {
  useFileUpload,
  IMAGE_UPLOAD_TOOLTIP,
  ICON_UPLOAD_TOOLTIP,
  VIDEO_UPLOAD_TOOLTIP,
  UploadTypes
} from '@/hooks/useFileUpload'

import { useUserStore } from '@/store/modules/user'

export default defineComponent({
  name: 'ContentForm',
  components: {
    LinkUrlInput,
    FileUpload,
    VideoSelector,
    VastTagInput,
    RemoteVideoInput,
    SkipOffsetInput,
    CodeInput
  },
  props: {
    modelValue: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  setup(props, context) {
    const userStore = useUserStore()

    const { isStaff } = userStore
    const { isDesktop } = useResize()
    const form = ref({ ...props.modelValue })

    // Local state for sub_type toggle (synced with form.sub_type for validation)
    const selectedSubType = ref('url')

    const { validationErrors } = useValidation(FlatDealValidationErrorsKey)
    const errors = validationErrors()

    const videoType = computed(() => {
      return [AdType.Slider, AdType.Overlay, AdType.OutStream].includes(
        form.value.type_id
      )
    })

    const extendedSettingsAllowed = computed(() => {
      if (!userStore.extendedSettingsAllowed) {
        return false
      }
      return ![AdType.Popunder, AdType.Interstitial].includes(
        form.value.type_id
      )
    })

    const linkActive = computed(() => {
      return (
        (videoType.value && VideoSource.Tag !== form.value.video_source) ||
        !videoType.value
      )
    })

    const shouldShowLinkUrl = computed(() => {
      if (isStaff && form.value.type_id === AdType.Banner) {
        return selectedSubType.value === 'url'
      }
      return true
    })

    const shouldShowCode = computed(() => {
      if (isStaff && form.value.type_id === AdType.Banner) {
        return selectedSubType.value === 'code'
      }
      return isStaff && form.value.type_id !== AdType.Banner
    })

    const update = () => {
      context.emit('update', {})
    }

    const onSubTypeChange = () => {
      context.emit('subTypeChange', selectedSubType.value)
      update()
    }

    const linkLabel = computed(() => {
      if (
        [
          AdType.Banner,
          AdType.Overlay,
          AdType.Slider,
          AdType.OutStream
        ].includes(form.value.type_id)
      ) {
        return 'Click URL'
      }
      return 'Link URL'
    })

    const handleFileChange = (
      file: any,
      ref: any,
      list: any[],
      type: UploadTypes
    ) => {
      if (list.length > 1) list.shift()

      const isValid = useFileUpload(file, ref, type, errors)
      if (isValid) {
        errors.value = {}
        form.value[type] = file.raw
      }

      update()
    }

    watch(
      (_: any) => props.modelValue,
      (val: any) => {
        form.value = val
      }
    )

    return {
      videoType,
      AdType,
      form,
      linkLabel,
      extendedSettingsAllowed,
      linkActive,
      shouldShowLinkUrl,
      shouldShowCode,
      handleFileChange,
      FlatDealType,
      isStaff,
      VideoSource,
      isDesktop,
      update,
      errors,
      selectedSubType,
      onSubTypeChange,
      IMAGE_UPLOAD_TOOLTIP,
      ICON_UPLOAD_TOOLTIP,
      VIDEO_UPLOAD_TOOLTIP
    }
  }
})
</script>

<style lang="scss" scoped>
.logo-container {
  height: 70%;
}
.logo {
  max-width: -webkit-fill-available;
  max-height: 100%;
}
.flex-container {
  display: flex;
  justify-content: space-between;
  &.center {
    justify-content: center;
  }
  &.around {
    justify-content: space-around;
  }
}
.footer {
  padding: 10px 5px 0 5px;
  border-top: 1px solid #e6ebf5;
}
.body {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}
.el-form {
  margin: 0 20px;
}
.el-card {
  padding-bottom: 5px;
  margin-top: 15px;
  &:hover {
    cursor: pointer;
  }
  &.disabled {
    opacity: 0.4;
  }
}
:deep(.el-alert) {
  margin-bottom: 10px;
}
</style>
